import {
	createContext,
	FC,
	ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from 'react';
import { useSubscription } from 'observable-hooks';
import { RxCollection } from 'rxdb';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';

import { ITypedAction } from '@mopla/utils';

import {
	EFeatureFlagActionTypes,
	fetchFeatureFlags,
} from '../actions/featureFlagActions';
import { UserActionTypes } from '../actions/userActions';
import { useBusinessLayer } from '../business-logic';
import {
	DEFAULT_FEATURE_FLAGS,
	TFeatureFlagValue,
} from '../constants/featureFlagConstants';
import { FEATURE_FLAGS_DOC_ID } from '../entities/featureFlag';
import { FeatureFlagDocument } from '../schemas/featureFlagSchema';

import { useFeatureFlagDevOverridesStore } from './useFeatureFlagDevOverridesStore';

export interface FeatureFlagContextType {
	// Core feature flag methods
	getFlag: (flagKey: string, defaultValue: boolean) => boolean;
	getStringListFlag: (
		flagKey: string,
		defaultValue: readonly string[]
	) => readonly string[];
	isDriverBookingCodeEnabled: () => boolean;
	isNewActivitiesEnabled: () => boolean;

	// API integration
	refreshFeatureFlags: () => void;
	isLoading: boolean; // true only on first load when no RxDB document exists
	isFetching: boolean; // true during any API call

	// Development helpers
	updateFlag: (flagKey: string, value: TFeatureFlagValue) => void;
	resetOverrides: () => void;
	getAllFlags: () => Record<string, TFeatureFlagValue>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(
	undefined
);

interface FeatureFlagProviderProps {
	children: ReactNode;
	localFlags?: Record<string, TFeatureFlagValue>;
}

/** Stable reference for default localFlags to prevent unnecessary re-renders
 * Using a constant reference instead of inline {} ensures referential equality
 * across renders when localFlags prop is not provided
 */
const __defaultLocalFlags = {};

/**
 * Feature Flag Provider for simple boolean feature flags
 * Integrates with backend API for real-time feature flag management
 */
export const FeatureFlagProvider: FC<FeatureFlagProviderProps> = ({
	children,
	localFlags = __defaultLocalFlags,
}) => {
	const { watchActions$, dispatch, db } = useBusinessLayer();
	const [isFetching, setIsFetching] = useState(false);
	const [hasDocument, setHasDocument] = useState(false);
	const { devOverrides, setOverride, resetOverrides } =
		useFeatureFlagDevOverridesStore();
	/** Same merge order as the RxDB stream - no all-false window before its first emission */
	const [flags, setFlags] = useState<Record<string, TFeatureFlagValue>>(() => ({
		...DEFAULT_FEATURE_FLAGS,
		...localFlags,
		...devOverrides,
	}));

	const fetchingActions$ = useMemo(
		() =>
			watchActions$([
				EFeatureFlagActionTypes.FetchFeatureFlags,
				EFeatureFlagActionTypes.FetchFeatureFlagsResult,
			]).pipe(
				map(({ type }) => type === EFeatureFlagActionTypes.FetchFeatureFlags),
				startWith(false),
				distinctUntilChanged()
			),
		[watchActions$]
	);

	useSubscription(fetchingActions$, setIsFetching);

	const userResultActions$ = useMemo(
		() => watchActions$([UserActionTypes.FetchUserResult]),
		[watchActions$]
	);

	const handleUserResult = useCallback(
		(action: ITypedAction<UserActionTypes, any>) => {
			if (
				action.type === UserActionTypes.FetchUserResult &&
				!action.payload?.error
			) {
				dispatch(fetchFeatureFlags());
			}
		},
		[dispatch]
	);

	useSubscription(userResultActions$, handleUserResult);

	const featureFlags$ = useMemo(() => {
		return (db['featureflags'] as RxCollection<FeatureFlagDocument>)
			.findOne(FEATURE_FLAGS_DOC_ID)
			.$.pipe(
				map((doc) => ({
					hasDoc: !!doc,
					featureFlags: doc
						? {
								/** Merge order (highest to lowest priority):
								 * 1. devOverrides - in-memory dev overrides
								 * 2. localFlags - environment-specific flags
								 * 3. doc.flags - persted API flags
								 */
								...doc.flags,
								...localFlags,
								...devOverrides,
						  }
						: /* No document exists yet (first app launch), merge defaults with local flags */
						  {
								...DEFAULT_FEATURE_FLAGS,
								...localFlags,
								...devOverrides,
						  },
				}))
			);
	}, [db, devOverrides, localFlags]);

	const onFeatureFlagsEmitHandler = useCallback(
		(data: {
			hasDoc: boolean;
			featureFlags: Record<string, TFeatureFlagValue>;
		}) => {
			setHasDocument(data.hasDoc);
			setFlags(data.featureFlags);
		},
		[]
	);

	useSubscription(featureFlags$, onFeatureFlagsEmitHandler);

	const isLoading = !hasDocument && isFetching;

	// Note: Feature flags are now loaded immediately from RxDB (no visual flash)
	// API updates are stored in RxDB and automatically sync to React state

	const getFlag = useCallback(
		(flagKey: string, defaultValue: boolean): boolean => {
			const value = flags[flagKey];
			return typeof value === 'boolean' ? value : defaultValue;
		},
		[flags]
	);

	const getStringListFlag = useCallback(
		(flagKey: string, defaultValue: readonly string[]): readonly string[] => {
			const value = flags[flagKey];
			return Array.isArray(value) ? value : defaultValue;
		},
		[flags]
	);

	const isDriverBookingCodeEnabled = useCallback((): boolean => {
		return getFlag('isDriverBookingCodeEnabled', false);
	}, [getFlag]);

	const isNewActivitiesEnabled = useCallback((): boolean => {
		return getFlag('isNewActivitiesEnabled', false);
	}, [getFlag]);

	const refreshFeatureFlags = useCallback(() => {
		dispatch(fetchFeatureFlags());
	}, [dispatch]);

	const getAllFlags = useCallback(() => {
		return { ...flags };
	}, [flags]);

	const contextValue: FeatureFlagContextType = useMemo(
		() => ({
			getFlag,
			getStringListFlag,
			isDriverBookingCodeEnabled,
			isNewActivitiesEnabled,
			updateFlag: setOverride,
			resetOverrides,
			getAllFlags,
			refreshFeatureFlags,
			isLoading,
			isFetching,
		}),
		[
			getFlag,
			getStringListFlag,
			isDriverBookingCodeEnabled,
			isNewActivitiesEnabled,
			setOverride,
			resetOverrides,
			getAllFlags,
			refreshFeatureFlags,
			isLoading,
			isFetching,
		]
	);

	return (
		<FeatureFlagContext.Provider value={contextValue}>
			{children}
		</FeatureFlagContext.Provider>
	);
};

export const useFeatureFlag = () => {
	const context = useContext(FeatureFlagContext);

	if (context === undefined) {
		throw new Error('useFeatureFlag must be used within a FeatureFlagProvider');
	}

	return context;
};
